import { AmountSuperScript } from "components/design/AmountSuperScript";
import { TableUI } from "components/design/TableUI";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import Loader from "components/DesignSystem/Loader/loader";
import {
  PRICE_FREQUENCY_LABEL,
  PriceFrequency,
} from "constants/productPriceFrequency";
import { PRODUCT_SERVICE_ITEM_TYPE } from "dictionaries";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { useToast } from "hooks/useToast";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  useGetCustomerDefaultPricesQuery,
  useRemoveCustomerDefaultPriceMutation,
} from "store/apis/invoices";
import { ItemType } from "types/Models/productAndServices";
import { BackendError } from "types/utils/error";
import ThreeDotsIcon from "../../../static/images/ThreeDots.svg";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Wind from "components/icons/Wind";

type CustomerDefaultPrice = {
  uuid: string;
  customer_id: string;
  product_id: string;
  product_name: string;
  product_type: ItemType;
  product_category_name: string;
  product_price: {
    uuid: string;
    price: number;
    description: string;
    frequency: string;
  };
};

export const CustomerDefaultPrices = ({
  customerId,
  entityId,
}: {
  customerId: string;
  entityId: string;
}) => {
  const { alertToast, successToast } = useToast();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.REVENUE
  );

  const {
    data: defaultPrices = [],
    isLoading,
    isSuccess,
  } = useGetCustomerDefaultPricesQuery(
    { entityId, customerId },
    { skip: !entityId || !customerId }
  );

  const [removeDefaultPrice, { isLoading: isRemoving }] =
    useRemoveCustomerDefaultPriceMutation();

  const handleRemoveDefaultPrice = async (item: CustomerDefaultPrice) => {
    try {
      await removeDefaultPrice({
        entityId,
        customerId,
        priceId: item.product_price.uuid,
        productId: item.product_id,
      }).unwrap();
      successToast({ message: "Default price removed successfully" });
    } catch (error) {
      alertToast(
        { message: (error as BackendError)?.data?.error?.message },
        error as Error
      );
    }
  };

  const createDefaultPriceColumn = createColumnHelper<CustomerDefaultPrice>();

  const columns = [
    createDefaultPriceColumn.display({
      id: "index",
      header: "No.",
      size: 5,
      cell: (info) => (
        <span className="t-text-body">{info.row.index + 1}.</span>
      ),
    }),
    createDefaultPriceColumn.accessor("product_name", {
      header: "Name",
      size: 25,
      cell: (info) => (
        <span className="t-text-body t-font-medium">{info.getValue()}</span>
      ),
    }),
    createDefaultPriceColumn.accessor("product_type", {
      header: "Type",
      size: 15,
      cell: (info) => (
        <span className="t-text-body">
          {PRODUCT_SERVICE_ITEM_TYPE[info.getValue()]}
        </span>
      ),
    }),
    createDefaultPriceColumn.accessor("product_price.frequency", {
      header: "Frequency",
      size: 15,
      cell: (info) => (
        <span className="t-text-body">
          {PRICE_FREQUENCY_LABEL[info.getValue() as PriceFrequency] ||
            info.getValue()}
        </span>
      ),
    }),
    createDefaultPriceColumn.accessor("product_category_name", {
      header: "Group",
      size: 15,
      cell: (info) => (
        <span className="t-text-body">{info.getValue() || "-"}</span>
      ),
    }),
    createDefaultPriceColumn.accessor("product_price.price", {
      header: "Default Price",
      size: 15,
      cell: (info) => (
        <span className="t-text-body">
          <AmountSuperScript amount={info.getValue()} />
        </span>
      ),
    }),
    ...(hasWritePermission
      ? [
          createDefaultPriceColumn.display({
            id: "actions",
            header: "",
            size: 5,
            cell: (info) => (
              <Dropdown.Root>
                <Dropdown.Trigger asChild>
                  <Button customType="ghost_icon" size="small">
                    <img src={ThreeDotsIcon} alt="Three dots" />
                  </Button>
                </Dropdown.Trigger>
                <Dropdown.Portal>
                  <Dropdown.Content align="end">
                    <Dropdown.Item
                      onSelect={() =>
                        handleRemoveDefaultPrice(info.row.original)
                      }
                      disabled={isRemoving}
                    >
                      <span className="t-text-red-70 t-text-body">
                        Remove as default price
                      </span>
                    </Dropdown.Item>
                  </Dropdown.Content>
                </Dropdown.Portal>
              </Dropdown.Root>
            ),
          }),
        ]
      : []),
  ];

  const table = useReactTable({
    data: defaultPrices,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 5,
    },
  });

  return (
    <Async.Root
      isLoading={isLoading}
      isSuccess={isSuccess}
      isEmpty={defaultPrices?.length === 0}
      renderOnLoad={
        <div className="t-flex t-justify-center t-items-center t-h-72">
          <Loader size="small" />
        </div>
      }
    >
      <Async.Empty>
        <div className="t-flex t-flex-col t-items-center t-justify-center t-h-72 t-text-neutral-40 t-gap-3">
          <Wind />
          <p className="t-m-0 t-text-body ">No default prices added so far</p>
        </div>
      </Async.Empty>
      <Async.Success>
        <TableUI table={table} isHeaderCellUpperCase={false} />
      </Async.Success>
    </Async.Root>
  );
};
